import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchNewsletters, createNewsletterWithCarryForward } from '@/store/slices/newslettersSlice';
import { fetchActionItems } from '@/store/slices/actionItemsSlice';
import { ROUTES } from '@/constants/routes';
import { EmptyState, ErrorState, LoadingState, ConfirmDialog } from '@/components/common/Common';
import NewsletterCard from '@/components/newsletter/NewsletterCard';
import { useNewsletterDelete } from '@/hooks/useNewsletterDelete';
import { isOverdue } from '@/utils/dateUtils';

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h1" sx={{ color: color ?? 'text.primary' }}>
        {value}
      </Typography>
    </Paper>
  );
}

export default function DashboardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items: newsletters, status, error } = useAppSelector((state) => state.newsletters);
  const actionItems = useAppSelector((state) => state.actionItems.items);
  const { pendingDelete, requestDelete, confirmDelete, cancelDelete } = useNewsletterDelete();

  useEffect(() => {
    dispatch(fetchNewsletters());
    dispatch(fetchActionItems());
  }, [dispatch]);

  const handleCreate = async () => {
    const result = await dispatch(createNewsletterWithCarryForward()).unwrap();
    navigate(ROUTES.newsletterEdit(result.newsletter.id));
  };

  if (status === 'loading' || status === 'idle') return <LoadingState label="Loading dashboard…" />;
  if (status === 'failed') return <ErrorState message={error ?? 'Failed to load'} onRetry={() => dispatch(fetchNewsletters())} />;

  const drafts = newsletters.filter((n) => n.status === 'draft').length;
  const published = newsletters.filter((n) => n.status === 'published').length;
  const openItems = actionItems.filter((i) => i.status !== 'completed');
  const overdueItems = openItems.filter((i) => isOverdue(i.dueDate, false));
  const recent = newsletters.slice(0, 4);

  return (
    <Stack spacing={4}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
        <Box>
          <Typography variant="h1">Dashboard</Typography>
          <Typography color="text.secondary">Company progress at a glance.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New Newsletter
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Issues" value={newsletters.length} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Drafts" value={drafts} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Published" value={published} color="success.main" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Open Action Items" value={openItems.length} color={overdueItems.length > 0 ? 'error.main' : undefined} />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Recent Issues
        </Typography>
        {recent.length === 0 ? (
          <EmptyState
            title="No newsletters yet"
            description="Create your first issue to start tracking company progress."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                New Newsletter
              </Button>
            }
          />
        ) : (
          <Stack spacing={2}>
            {recent.map((newsletter) => (
              <NewsletterCard key={newsletter.id} newsletter={newsletter} onDelete={requestDelete} />
            ))}
          </Stack>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete newsletter?"
        message={`"${pendingDelete?.title}" and its action items will be permanently removed.`}
        confirmLabel="Delete"
        destructive
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
    </Stack>
  );
}
